import React, { useState, useContext, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebookF,
  faTwitter,
  faInstagram,
  faTiktok,
  faYoutube,
  faLinkedin,
} from '@fortawesome/free-brands-svg-icons';
import { faSearch, faBars, faTimes, faUser } from '@fortawesome/free-solid-svg-icons';

import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { AuthContext } from '../Context/AuthContext';
import logo from '../assets/Icons/logo.svg';
import LanguageSwitcher from './LanguageSelector';
import LoginModal from './Pages/LoginPage';

const Navbar = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext) || {};
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const navbarRef = useRef(null);
  const searchRef = useRef(null);
  const userDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
      
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        const isSearchInput = event.target.closest('input[type="text"]');
        const isSearchButton = event.target.closest('button[aria-label="Search"]');
        
        if (!isSearchInput && !isSearchButton) {
          setSearchOpen(false);
        }
      }
      
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setUserDropdownOpen(false);
    navigate('/');
  };

  const navLinks = [
    { path: "/About Us", label: "About Us" },
    { path: "/Service", label: "Services" },
    { path: "/blog", label: "Blog" },
    { path: "/Connect", label: "Connect" }
  ];

  const getDisplayName = () => {
    if (!user) return '';
    if (user.name) return user.name;
    if (user.email) return user.email.split('@')[0];
    if (user.first_name) {
      return user.last_name ? `${user.first_name} ${user.last_name}` : user.first_name;
    }
    return 'User';
  };

  const getProfilePicture = () => {
    if (!user) return null;
    if (user.picture) return user.picture;
    if (user.avatar) return user.avatar;
    return null;
  };

  return (
    <div className="mb-4 sm:mb-4">
      <nav 
        ref={navbarRef}
        className="fixed top-0 left-0 right-0 z-50 /90 border-b bg-white border-gray-100 "
      >
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <RouterLink to="/" className="flex-shrink-0 ">
              <img 
                className="h-8 w-auto md:w-28" 
                src={logo} 
                title='Ato creative Logo'
                alt="Ato Creative logo"
                onClick={() => setMobileMenuOpen(false)}
              />
            </RouterLink>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <RouterLink
                  key={link.path}
                  to={link.path}
                  className="text-[#FD5F20] hover:text-[#FD5F20] transition-colors duration-200 font-extrabold text-sm uppercase tracking-wider"
                >
                  {link.label}
                </RouterLink>
              ))}
            </div>

            {/* Desktop Right Side */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Search Button */}
              <div className="relative" ref={searchRef}>
                <button 
                  onClick={() => setSearchOpen(!searchOpen)}
                  className="p-2 text-[#FD5F20] hover:text-[#FD5F20] transition-colors"
                  aria-label="Search"
                >
                  <FontAwesomeIcon icon={faSearch} />
                </button>
                {searchOpen && (
                  <div className="absolute right-0 mt-2 w-72 rounded-lg p-2 z-50">
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search..."
                        className="w-full pl-3 pr-10 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FD5F20]/50"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <button 
                        className="absolute right-2 top-2 text-gray-400 hover:text-[#FD5F20]"
                        onClick={() => setSearchOpen(false)}
                      >
                        <FontAwesomeIcon icon={faTimes} size="xs" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* Social Media Links */}
              <div className="flex space-x-4">
                <a href="https://www.facebook.com/profile.php?id=61561831803941" className="text-[#FD5F20] hover:text-[#FD5F20]/90 transition-colors">
                  <FontAwesomeIcon icon={faFacebookF} />
                </a>
                <a href="https://www.instagram.com/atocreative_et?igsh=MzRlODBiNWFlZA==" className="text-[#FD5F20] hover:text-[#FD5F20]/90 transition-colors">
                  <FontAwesomeIcon icon={faInstagram} />
                </a>
                <a href="https://www.tiktok.com/@atocreative_et?_t=ZM-8wk0HW3Vwhx&_r=1" className="text-[#FD5F20] hover:text-[#FD5F20]/90 transition-colors">
                  <FontAwesomeIcon icon={faTiktok} />
                </a>
                <a href="https://youtube.com/@atocreative_et?si=eCyJGOl0_WcVT8Ta" className="text-[#FD5F20] hover:text-[#FD5F20]/90 transition-colors">
                  <FontAwesomeIcon icon={faYoutube} />
                </a>
              </div>

              {/* User/Auth */}
              {user ? (
                <div className="relative flex items-center gap-4" ref={userDropdownRef}>
                  <span className="text-sm font-medium text-gray-700">
                    Hi, {getDisplayName()}
                  </span>
                  <button
                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    className="flex items-center space-x-1 text-gray-700 hover:text-[#FD5F20] transition-colors"
                  >
                    {getProfilePicture() ? (
                      <img 
                        src={getProfilePicture()} 
                        alt="User profile" 
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                        <FontAwesomeIcon icon={faUser} className="text-[#FD5F20]" />
                      </div>
                    )}
                  </button>
                  {userDropdownOpen && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                      <RouterLink
                        to="/dashboard"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setUserDropdownOpen(false)}
                      >
                        Dashboard
                      </RouterLink>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => {
                    setAuthModalOpen(true);
                    navigate('/communitytool'); // Redirect to CommunityTool
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-[#FD5F20] to-[#FF8C00] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Get Started
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-4">
              <button 
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 text-[#FD5F20] hover:text-[#FD5F20] transition-colors"
                aria-label="Search"
              >
                <FontAwesomeIcon icon={faSearch} />
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-[#FD5F20] hover:text-[#FD5F20] transition-colors"
                aria-label="Menu"
              >
                <FontAwesomeIcon icon={mobileMenuOpen ? faTimes : faBars} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden ${mobileMenuOpen ? 'block' : 'hidden'}`}>
          <div className="px-4 pt-2 pb-4 space-y-2 bg-[#FD5F20]/60 border-t border-gray-100 text-gray-100">
            {navLinks.map((link) => (
              <RouterLink
                key={link.path}
                to={link.path}
                className="block px-3 py-2 rounded-md text-gray-700 hover:text-[#FD5F20] hover:bg-gray-50 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </RouterLink>
            ))}
            
            {/* Mobile Social Media Links */}
            <div className="px-3 py-4 flex justify-center space-x-6">
              <a href="https://www.facebook.com/profile.php?id=61561831803941" className="hover:text-white">
                <FontAwesomeIcon icon={faFacebookF} size="lg" />
              </a>
              <a href="https://www.instagram.com/atocreative_et?igsh=MzRlODBiNWFlZA==" className="hover:text-white">
                <FontAwesomeIcon icon={faInstagram} size="lg" />
              </a>
              <a href="https://www.tiktok.com/@atocreative_et?_t=ZM-8wk0HW3Vwhx&_r=1" className="hover:text-white">
                <FontAwesomeIcon icon={faTiktok} size="lg" />
              </a>
              <a href="https://youtube.com/@atocreative_et?si=eCyJGOl0_WcVT8Ta" className="hover:text-white">
                <FontAwesomeIcon icon={faYoutube} size="lg" />
              </a>
            </div>

            <div className="px-3 py-2">
              <LanguageSwitcher mobile alt="Language" title="language"  />
            </div>
            {user ? (
              <>
                <div className="px-3 py-2 text-white">
                  Welcome, {getDisplayName()}
                </div>
                <RouterLink
                  to="/dashboard"
                  className="block px-3 py-2 rounded-md text-gray-700 hover:text-[#FD5F20] hover:bg-gray-50 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </RouterLink>
                <button
                  onClick={handleLogout}
                  className="w-full mt-2 px-4 py-2 bg-white text-[#FD5F20] rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setAuthModalOpen(true);
                  navigate('/communitytool'); // Redirect to CommunityTool
                }}
                className="w-full mt-2 px-4 py-2 bg-gradient-to-r from-[#FD5F20] to-[#FF8C00] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Get Started
              </button>
            )}
          </div>
        </div>

        {/* Mobile Search */}
        {searchOpen && (
          <div className="md:hidden px-4 py-3 border-t border-gray-100" ref={searchRef}>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-3 pr-10 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FD5F20]/50"
                onClick={(e) => e.stopPropagation()}
              />
              <button 
                className="absolute right-2 top-2 text-gray-400 hover:text-[#FD5F20]"
                onClick={() => setSearchOpen(false)}
              >
                <FontAwesomeIcon icon={faTimes} size="xs" />
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Login Modal */}
      {/* <LoginModal 
        isOpen={authModalOpen} 
        onClose={() => setAuthModalOpen(false)} 
      /> */}
    </div>
  );
};

export default Navbar;